import { defineStore } from 'pinia';
import axios from 'axios';

// Recycle Bin admin store — generic across every module (tab) registered on
// the backend in config/recycle.php. Mirrors useBlogStore conventions:
// server-side paginated + filterable.
export const useRecycleBinStore = defineStore('recycleBin', {
    state: () => ({
        modules: [],
        modulesLoading: false,
        activeModule: 'reservations',

        items: [],
        pagination: null,
        loading: false,
        acting: false,

        deleters: [],
        selected: [],

        filters: { search: '', date_from: '', date_to: '', deleted_by: '', sort: '', direction: 'desc', per_page: 20 },
    }),

    actions: {
        async fetchModules() {
            this.modulesLoading = true;
            try {
                const { data } = await axios.get('/admin/recycle/modules');
                this.modules = data.data ?? [];
            } finally {
                this.modulesLoading = false;
            }
        },

        setModule(key) {
            this.activeModule = key;
            this.items = [];
            this.pagination = null;
            this.selected = [];
            this.deleters = [];
            this.filters = { search: '', date_from: '', date_to: '', deleted_by: '', sort: '', direction: 'desc', per_page: 20 };
        },

        async fetchItems(page = 1) {
            this.loading = true;
            try {
                const params = { page, per_page: this.filters.per_page };
                if (this.filters.search) params.search = this.filters.search;
                if (this.filters.date_from) params.date_from = this.filters.date_from;
                if (this.filters.date_to) params.date_to = this.filters.date_to;
                if (this.filters.deleted_by) params.deleted_by = this.filters.deleted_by;
                if (this.filters.sort) params.sort = this.filters.sort;
                if (this.filters.direction) params.direction = this.filters.direction;
                const { data } = await axios.get(`/admin/recycle/${this.activeModule}`, { params });
                this.items = data.data.data ?? [];
                this.pagination = data.data;
                this.selected = [];
            } finally {
                this.loading = false;
            }
        },

        async fetchDeleters() {
            try {
                const { data } = await axios.get(`/admin/recycle/${this.activeModule}/deleters`);
                this.deleters = data.data ?? [];
            } catch {
                this.deleters = [];
            }
        },

        async restore(id) {
            this.acting = true;
            try {
                await axios.post(`/admin/recycle/${this.activeModule}/${id}/restore`);
                this.items = this.items.filter((i) => i.id !== id);
                this.selected = this.selected.filter((sid) => sid !== id);
            } finally {
                this.acting = false;
            }
        },

        async forceDelete(id) {
            this.acting = true;
            try {
                await axios.delete(`/admin/recycle/${this.activeModule}/${id}/force`);
                this.items = this.items.filter((i) => i.id !== id);
                this.selected = this.selected.filter((sid) => sid !== id);
            } finally {
                this.acting = false;
            }
        },

        async restoreSelected() {
            if (!this.selected.length) return;
            this.acting = true;
            try {
                await axios.post(`/admin/recycle/${this.activeModule}/restore-selected`, { ids: this.selected });
                this.items = this.items.filter((i) => !this.selected.includes(i.id));
                this.selected = [];
            } finally {
                this.acting = false;
            }
        },

        async forceDeleteSelected() {
            if (!this.selected.length) return;
            this.acting = true;
            try {
                await axios.delete(`/admin/recycle/${this.activeModule}/delete-selected`, { data: { ids: this.selected } });
                this.items = this.items.filter((i) => !this.selected.includes(i.id));
                this.selected = [];
            } finally {
                this.acting = false;
            }
        },

        async restoreAll() {
            this.acting = true;
            try {
                await axios.post(`/admin/recycle/${this.activeModule}/restore-all`);
                await this.fetchItems(1);
            } finally {
                this.acting = false;
            }
        },

        async emptyBin() {
            this.acting = true;
            try {
                await axios.delete(`/admin/recycle/${this.activeModule}/empty`);
                await this.fetchItems(1);
            } finally {
                this.acting = false;
            }
        },

        toggleSelected(id) {
            const idx = this.selected.indexOf(id);
            if (idx === -1) this.selected.push(id);
            else this.selected.splice(idx, 1);
        },

        toggleSelectAll() {
            this.selected = this.selected.length === this.items.length ? [] : this.items.map((i) => i.id);
        },
    },
});
